
import Vue, { PropType } from 'vue';
import Api from '@/core/api';

import addEventListener from '@/common/add-event-listener';

import { isFunction, isNumber, isString } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import { PlayerState } from '@/core/player-state';

export default Vue.extend({
    name: 'Shortcuts',

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        }
    },

    data() {
        return {
            unsubscribers: [] as Unsubscriber[],
        };
    },


    mounted(): void {
        this.unsubscribers.push(
            addEventListener(document, 'keydown', (e: Event) => this.keyDownHandler(e as KeyboardEvent))
        );
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    methods: {
        keyDownHandler(e: KeyboardEvent) {
            if (!e || !isString(e.key)) {
                return;
            }

            const state: PlayerState = this.api.getState();

            // 'isVideoElementFullScreen' can currently only be true for non-live session type videos, or on mobile
            if (!state.isVideoElementFullScreen) {
                // TODO: make shortcuts available out of fullscreen mode
                // This is currently disabled to prevent triggering shortcuts when user is typing in other inputs,
                // or to prevent triggering the wrong video's shortcuts
                return;
            }

            /// ///////////
            // Arrows
            /// ///////////

            if (e.key === 'ArrowUp') { return this.onArrowUpPressed(state); }
            if (e.key === 'ArrowDown') { return this.onArrowDownPressed(state); }
            if (e.key === 'ArrowRight') { return this.onArrowRightPressed(state); }
            if (e.key === 'ArrowLeft') { return this.onArrowLeftPressed(state); }

            /// ///////////
            // Special keys
            /// ///////////

            if (e.key === 'Escape') { return this.onEscapePressed(state); }
            if (e.key === ' ') { return this.onSpacePressed(state); }
            if (e.key === 'Enter') { return this.onEnterPressed(state); }

            /// ///////////
            // Letters
            /// ///////////

            if (e.key === 'c' || e.key === 'C') { return this.onCPressed(); }
            if (e.key === 'f' || e.key === 'F') { return this.onFPressed(state); }
            if (e.key === 'm' || e.key === 'M') { return this.onMPressed(state); }

            /// ///////////
            // Numbers
            /// ///////////

            if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(e.key)) {
                return this.onNumberPressed(state, parseInt(e.key));
            }
        },

        /// ///////////
        // Special keys
        /// ///////////

        onEscapePressed(state: PlayerState) {
            if (state.isVideoElementFullScreen) {
                this.api.exitFullScreen();
            }
        },

        onSpacePressed(state: PlayerState) {
            console.debug('[Shortcuts] play/pause onSpacePressed');
            state.isPlaying ? this.api.pause() : this.api.play();
        },

        onEnterPressed(state: PlayerState) {
            console.debug('[Shortcuts] play/pause onEnterPressed');
            state.isPlaying ? this.api.pause() : this.api.play();
        },

        /// ///////////
        // Letters
        /// ///////////

        onCPressed() {
            // TODO: toggle captions
        },

        onFPressed(state: PlayerState) {
            state.isVideoElementFullScreen ? this.api.exitFullScreen() : this.api.enterFullScreen();
        },

        onMPressed(state: PlayerState) {
            state.muted ? this.api.unmute() : this.api.mute();
        },

        /// ///////////
        // Arrows
        /// ///////////

        onArrowUpPressed(state: PlayerState) {
            this.api.changeVolume(state.volume + 0.1);
        },
        onArrowDownPressed(state: PlayerState) {
            this.api.changeVolume(state.volume - 0.1);
        },
        onArrowLeftPressed(state: PlayerState) {
            this.api.seek(state.videoElapsedSecs - 5);
        },
        onArrowRightPressed(state: PlayerState) {
            this.api.seek(state.videoElapsedSecs + 5);
        },

        /// ///////////
        // Numbers
        /// ///////////

        onNumberPressed(state: PlayerState, number: number) {
            const duration = state?.videoDurationSecs;
            if (isNumber(duration)) {
                this.api.seek(duration * number / 10);
            }
        }
    }
});
